/**
 * Content Rewrite 3.0 — prose-first textbook for Uczta dla Odyna.
 * ONE EXPANSION = ONE LONG CHAPTER (Norwegowie): internal headings only.
 * Action-board icon/cost catalogues, full occupation card texts and exact
 * goods tables remain PARTIAL — read from physical components. Do not invent.
 */

export type ManualSection = {
  id: string;
  title: string;
  body: string;
  keywords?: string[];
  requiredModuleIds: Array<'base' | 'norwegians' | 'solo'>;
};

export type ManualChapter = {
  id: string;
  title: string;
  summary: string;
  requiredModuleIds: Array<'base' | 'norwegians' | 'solo'>;
  sections: ManualSection[];
};

export const odinChapters: ManualChapter[] = [
  {
    id: 'goal-and-structure',
    title: '1. Cel gry i najważniejsza idea',
    summary: 'Jak zdobywa się punkty i dlaczego zakrywanie plansz jest sercem gry.',
    requiredModuleIds: ['base'],
    sections: [
      {
        id: 'goal',
        title: 'Cel gry',
        requiredModuleIds: ['base'],
        keywords: ['punkty', 'zwycięstwo', 'ujemne pola', 'osada', 'PARTIAL'],
        body: `Status treści: PARTIAL

Ta instrukcja uczy procedur gry podstawowej, dodatku Norwegowie i wariantu solo. Dokładne ikony i koszty każdego pola planszy akcji, pełny katalog kart pomocników oraz wszystkie wartości nadrukowane wyłącznie na kaflach odczytuj z fizycznych komponentów. Aplikacja nie inventuje brakujących katalogów.

Co robisz i po co

W Uczcie dla Odyna rozwijasz gospodarstwo wikińskiego wodza. Pozyskujesz żywność, przedmioty, zwierzęta, łodzie i budynki, handlujesz, polujesz, plądrujesz oraz odkrywasz wyspy. Zwycięża gracz z najwyższym wynikiem po ostatniej rundzie.

Najważniejszym źródłem punktów jest rozsądne gospodarowanie planszami przestrzennymi. Plansza osady, wyspy i budynki zawierają pola ujemnych punktów. Zakrywasz je kaflami dóbr. Jednocześnie starasz się odsłaniać lub utrzymywać dostęp do symboli dochodu oraz otaczać pola premii, aby co rundę otrzymywać dodatkowe dobra.

Nie wystarczy zebrać dużo elementów. Trzeba przekształcać je w układ, który ogranicza ujemne punkty, zwiększa dochód w srebrze, uruchamia premie, pozostawia dobre miejsce na przyszłe kafle i pozwala wykarmić biesiadników na koniec rundy.

Przykład przy stole

Masz dużo pomarańczowej żywności i zero miejsca na duży zielony kafel. Jeśli najpierw zapełnisz planszę małymi kaflami „byle czym”, nie zakryjesz późniejszej kary −8 i nie uruchomisz dochodu. Lepiej zostawić lukę pod większy kształt i wziąć akcję dającą właściwy kafel.

Typowy błąd

Traktować grę jak wyścig o jak najwięcej elementów bez planu przestrzennego. Punkty wychodzą z układu, nie z samej liczby żetonów.

Wyjątek i odsyłacz

Wartości nadrukowane na konkretnych polach, kartach i kaflach obowiązują z komponentu. Strukturę rundy opisuje rozdział „Przebieg rundy”, a układanie — „Układanie dóbr na planszach”.

Podsumowanie i mikro-FAQ

Cel: najwyższy wynik po ostatniej rundzie. P: Czy wystarczy zebrać dużo żywności? O: Nie — liczy się układ plansz i biesiada. P: Czy aplikacja zna koszt każdego pola akcji? O: Nie — PARTIAL; odczytuj ikony z planszy.`
      },
      {
        id: 'game-length',
        title: 'Liczba rund i tryby gry',
        requiredModuleIds: ['base'],
        keywords: ['6 rund', '7 rund', 'krótka', 'długa'],
        body: `Co to jest i po co

Długość partii wyznacza tor rund. Standardowa rozgrywka trwa 7 rund. Gra może również przewidywać krótszą konfigurację 6-rundową, oznaczoną odpowiednią stroną lub ustawieniem toru.

Kiedy używać której długości

Do pierwszej partii zalecana jest pełna gra 7-rundowa: rozwój jest czytelniejszy, a gracze mają czas zrozumieć zależności między łodziami, wyspami, emigracją i układaniem kafli. Wariant 6-rundowy stosujcie dopiero, gdy znacie rytm faz.

Procedura

1. Przed startem wybierzcie długość gry zgodną z torem rund / stroną komponentu.
2. Ustawcie znacznik na polu startowym wskazanym dla wybranej długości.
3. Każdą rundę rozgrywajcie według faz opisanych w rozdziale „Przebieg rundy”.
4. Po ostatniej rundzie przejdźcie do punktacji końcowej.

Przykład przy stole

Trójka graczy wybiera 7 rund i Norwegów. Znacznik stoi na polu rundy 1. Po rundzie 7 nie zaczynają ósmej — idą do notesu punktacji.

Typowy błąd

Skracać partię „na oko” bez użycia oficjalnego ustawienia 6-rundowego albo pomijać fazy końca rundy w ostatnich turach.

Wyjątek i odsyłacz

Ikony na torze rund są rozstrzygające dla żniw, broni i wzgórz. Szczegóły faz: rozdział 4. Solo: rozdział „Wariant jednoosobowy”.

Podsumowanie i mikro-FAQ

Standard = 7 rund; 6 rund tylko przy oficjalnym ustawieniu. P: Czy pierwsza partia powinna być 6-rundowa? O: Nie — zalecane 7. P: Czy każda runda ma te same fazy zasobów? O: Nie — tor pokazuje różnice.`
      },
      {
        id: 'why-space-matters',
        title: 'Dlaczego najpierw planujesz przestrzeń',
        requiredModuleIds: ['base'],
        keywords: ['planowanie', 'przestrzeń', 'początkujący', 'kolejność'],
        body: `Co to jest i po co

Uczta dla Odyna celowo nie nagradza samego zbierania dóbr. Każdy kafel ma kształt, kolor i przyszłe zastosowanie, więc decyzja o akcji jest dobra tylko wtedy, gdy wiesz, gdzie jej rezultat położysz.

Kiedy stosować tę zasadę

Przed każdym wyborem pola akcji — zwłaszcza w rundach 1–3, gdy plansza osady jest jeszcze pusta.

Procedura planowania

1. Wybierz jedno pole ujemne, które chcesz zakryć.
2. Wybierz jedną linię dochodu, którą chcesz rozwijać.
3. Zostaw jedno miejsce na większy kafel później.
4. Dopiero potem wybierz akcję dającą dobra, statek albo budynek.

Przykład przy stole

Duży kafel może od razu zakryć karę, lecz może też zablokować miejsce potrzebne do uruchomienia premii. Małe kafle są mniej widowiskowe, ale często domykają szczelinę albo rozdzielają sąsiadującą żywność.

Typowy błąd

Zapełnianie każdej wolnej kratki od razu. Nie musisz tego robić. Czasem lepiej zachować nieregularną lukę na późniejszy żeton specjalny.

Wyjątek i odsyłacz

Zasady stykania kolorów i srebra: rozdział „Układanie dóbr”. Dochód i premie: fazy w rozdziale „Przebieg rundy”.

Podsumowanie i mikro-FAQ

Najpierw przestrzeń, potem akcja. P: Czy muszę zakrywać wszystko od razu? O: Nie. P: Czy wikingowie to „punkty akcji do wydania”? O: Nie — to ludzie, którzy mają zdobyć elementy potrzebne osadzie.`
      },
    ],
  },
  {
    id: 'components-and-colors',
    title: '2. Dobra, kolory i elementy',
    summary: 'Znaczenie kolorów kafli, srebra, materiałów, zwierząt, łodzi i kart.',
    requiredModuleIds: ['base'],
    sections: [
      {
        id: 'goods-colors',
        title: 'Cztery grupy dóbr',
        requiredModuleIds: ['base'],
        keywords: ['pomarańczowe', 'czerwone', 'zielone', 'niebieskie', 'ulepszanie'],
        body: `Co to jest i po co

Kafle dóbr są dwustronne i tworzą łańcuch jakości. Kolor mówi, do czego kafel służy przy biesiadzie, układaniu i ulepszaniu.

Cztery grupy

Pomarańczowe dobra to podstawowa żywność. Najczęściej służą do biesiady i mogą być ulepszane.

Czerwone dobra to lepsza żywność oraz zwierzęta. Również nadają się do biesiady, ale przy układaniu na planszach obowiązują dla nich istotne ograniczenia sąsiedztwa.

Zielone dobra to przedmioty rzemieślnicze. Można nimi zakrywać plansze przestrzenne zgodnie z zasadami sąsiedztwa.

Niebieskie dobra to luksusowe towary. Są najbardziej elastyczne podczas układania i często zapewniają duże kafle lub wysoką wartość.

Ulepszanie

Ulepszenie dobra oznacza odwrócenie go na następną stronę łańcucha: zwykle pomarańczowe staje się czerwone, a czerwone zielone albo zielone niebieskie — zgodnie z konkretnym żetonem. Nie wolno dowolnie zamieniać kształtu ani wybierać innego żetonu. Odwracasz dokładnie ten sam kafel.

Przykład przy stole

Masz pomarańczowy kafel mleka. Pole ulepszenia pozwala odwrócić jedno dobro. Odwracasz mleko na jego czerwoną stronę. Nie bierzesz z zasobów innego czerwonego kafla „w zamian”.

Typowy błąd

Odwracać kafel „bo pasuje kolorem” bez efektu ulepszenia albo brać z puli nowy kafel zamiast odwrócić posiadany.

Wyjątek i odsyłacz

Dokładny łańcuch każdego żetonu odczytaj z fizycznego kafla — katalog pozostaje PARTIAL. Sąsiedztwo przy układaniu: rozdział 5. Biesiada: rozdział 4.

Podsumowanie i mikro-FAQ

Cztery kolory, dwustronne kafle, ulepszenie = odwrócenie tego samego żetonu. P: Czy mogę wybrać dowolną stronę przy położeniu? O: Nie bez ulepszenia. P: Czy czerwone zwierzęta to też czerwone dobra? O: Tak.`
      },
      {
        id: 'resources',
        title: 'Srebro, drewno, kamień i ruda',
        requiredModuleIds: ['base'],
        keywords: ['srebro', 'drewno', 'kamień', 'ruda', 'materiały budowlane', 'wzgórza', 'góry'],
        body: `Co to jest i po co

Srebro, drewno, kamień i ruda to zasoby inne niż kafle dóbr. Służą do płatności, budowy, modyfikacji rzutów (gdy pole na to pozwala) oraz wypełniania pojedynczych pól.

Srebro

Srebro jest walutą, ale również może wypełniać pojedyncze pola plansz podczas układania. Monety nie są kaflami dóbr i nie podlegają ulepszaniu.

Drewno, kamień, ruda

Drewno i kamień to materiały budowlane. Służą głównie do budowania łodzi i budynków, a niekiedy mogą pomagać w próbach łowieckich zgodnie z ikonami danej akcji.

Ruda jest szczególnie związana z łodziami i uzbrojeniem. Ruda położona na łodzi pozostaje na niej, zwiększa potencjał odpowiednich wypraw lub najazdów i może przynosić punkty, ale utrata albo emigracja łodzi może spowodować utratę znajdującej się na niej rudy.

Góry i wzgórza

Żeton wzgórz (gór) udostępnia materiały w kolejności przedstawionej na pasku. Gdy akcja pozwala pobrać zasoby ze wzgórza, bierzesz odpowiednią liczbę kolejnych dostępnych elementów od wskazanej strony. Nie wybierasz dowolnych symboli ze środka.

Przykład przy stole

Akcja góry pokazuje pobranie dwóch kolejnych zasobów. Na pasku leżą kolejno drewno, kamień, ruda. Bierzesz drewno i kamień — nie „przeskakujesz” do rudy.

Typowy błąd

Wybierać zasób ze środka paska albo używać drewna jako modyfikatora rzutu, gdy konkretne pole tego nie pokazuje.

Wyjątek i odsyłacz

Dokładne ikony pól gór i kosztów odczytuj z planszy akcji (PARTIAL). Faza odnawiania wzgórz: rozdział 4. Emigracja i ruda: rozdział 6.

Podsumowanie i mikro-FAQ

Srebro ≠ kafel dobra; ruda na łodzi może przepaść. P: Czy mogę ulepszyć monetę? O: Nie. P: Czy biorę dowolny symbol ze wzgórza? O: Nie — kolejność z paska.`
      },
      {
        id: 'ships',
        title: 'Łodzie',
        requiredModuleIds: ['base'],
        keywords: ['wielorybnik', 'knara', 'langskip', 'statek'],
        body: `Co to jest i po co

Trzy główne rodzaje łodzi otwierają różne rodziny akcji. Samo posiadanie statku nie wykonuje akcji — statek musi być dostępny przed rozpoczęciem akcji, chyba że konkretne pole wyraźnie pozwala najpierw go zbudować, a następnie natychmiast wykorzystać.

Rodzaje

Wielorybnik służy przede wszystkim do wielorybnictwa. Jest też wykorzystywany w części wypraw i — z dodatkiem Norwegowie — w małej emigracji.

Knara jest statkiem handlowym. Umożliwia handel zamorski, niektóre wyprawy oraz działania wskazane na planszy akcji.

Langskip jest okrętem wojennym. Umożliwia najazdy, plądrowanie i zdobywanie części wysp.

Przykład przy stole

Chcesz wykonać wielorybnictwo, ale masz tylko knarę. Najpierw budujesz wielorybnika (osobna akcja lub pole, które na to pozwala), potem — gdy masz statek — wybierasz pole wielorybnictwa.

Typowy błąd

Zakładać, że „jakaś łódź” wystarczy do każdej akcji morskiej. Rodzaj musi pasować do ikony pola.

Wyjątek i odsyłacz

Emigracja usuwa statek na stałe. Norwegowie: mała emigracja dotyczy wielorybnika — rozdział „Norwegowie”.

Podsumowanie i mikro-FAQ

Wielorybnik / knara / langskip = różne uprawnienia. P: Czy jedna łódź działa wiele razy? O: Tak, dopóki akcja jej nie usuwa. P: Czy ruda na łodzi zostaje po emigracji? O: Zwykle przepada.`
      },
      {
        id: 'occupations',
        title: 'Karty pomocników',
        requiredModuleIds: ['base'],
        keywords: ['karty', 'pomocnik', 'zawód', 'A', 'B', 'C', 'profesje'],
        body: `Co to jest i po co

Karty pomocników (zawodów / profesji) dają efekty stałe, jednorazowe albo uruchamiane w określonych sytuacjach. Każda karta wskazuje kategorię oraz koszt lub warunek zagrania.

Kiedy karta działa

Karta pozostająca na ręce nie działa. Trzeba ją zagrać dzięki polu akcji albo innemu efektowi. Treść i symbole na karcie mają pierwszeństwo przed ogólnym opisem, gdy karta wyraźnie ustanawia wyjątek.

Procedura zagrania

1. Spełnij koszt lub warunek wskazany na karcie / polu.
2. Zagraj kartę z ręki do swojego obszaru (lub zgodnie z tekstem karty).
3. Rozpatrz efekt dokładnie tak, jak napisano — bez domyślania dodatkowych korzyści.

Przykład przy stole

Pole pozwala zagrać pomocnika. Masz kartę dającą drewno przy budowie. Grasz ją teraz; efekt zaczyna obowiązywać od momentu wskazanego na karcie, nie „wstecz” na już wykonane budowy.

Typowy błąd

Zakładać, że kategoria A/B/C sama coś daje bez zagrania, albo rozszerzać efekt karty na podobne akcje (np. Najazd ↔ Grabież).

Wyjątek i odsyłacz

Pełne teksty kart są PARTIAL — obowiązuje nadruk. Kompatybilność z Norwegami: rozdział „Norwegowie”.

Podsumowanie i mikro-FAQ

Ręka ≠ efekt; nadruk karty wygrywa. P: Czy mogę zagrać kartę bez pola/efektu? O: Nie, chyba że karta sama na to pozwala. P: Czy aplikacja zastępuje katalog kart? O: Nie.`
      },
    ],
  },
  {
    id: 'setup',
    title: '3. Przygotowanie gry',
    summary: 'Kompletne przygotowanie wspólnego stołu i obszaru każdego gracza.',
    requiredModuleIds: ['base'],
    sections: [
      {
        id: 'common-setup',
        title: 'Przygotowanie wspólnego obszaru',
        requiredModuleIds: ['base'],
        keywords: ['setup', 'przygotowanie', 'plansza akcji', 'wzgórza', '1 gracz', '2 graczy', '3 graczy', '4 graczy'],
        body: `Co to jest i po co

Wspólny setup ustawia planszę akcji, tor rund, zasoby i wyspy tak, by wszyscy widzieli te same dostępne akcje. Błąd tutaj psuje koszty kolumn i dostępność wysp przez całą partię.

Procedura wspólna

1. Rozłóż właściwą planszę akcji i wybierz stronę odpowiadającą liczbie graczy.
2. Przygotuj tor rund dla wybranej długości gry.
3. Ułóż tacki lub zasoby kafli dóbr tak, aby obie strony każdego rodzaju były łatwe do rozpoznania.
4. Połóż w zasięgu wszystkich: srebro, drewno, kamień, rudę, karty broni, kości, łodzie, budynki i żetony specjalne.
5. Potasuj karty pomocników według kategorii i utwórz właściwe talie.
6. Przygotuj stos żetonów wzgórz. Odkryj początkowy żeton lub żetony wskazane przez planszę/tor rund.
7. Wyłóż dostępne plansze wysp.
8. Ustal pierwszego gracza i połóż znacznik pierwszego gracza przy jego obszarze.

Liczba graczy — strona planszy i rozszerzenia

• 1 gracz: strona / układ wskazany dla gry solo; dodaj drugi zestaw wikingów według rozdziału solo.
• 2 graczy: strona planszy akcji dla 2 graczy; wyłóż dostępne wyspy podstawowe zgodnie z liczbą graczy na komponencie.
• 3 graczy: strona dla 3 graczy; standardowy zestaw wysp podstawowych.
• 4 graczy (bez Norwegów): strona dla 4 graczy oraz elementy rozszerzające planszę akcji wskazane dla tej liczby. Nie używaj tych żetonów powtórzeń z dodatkiem Norwegowie — Norwegowie mają własne trzy plansze i zakazują powtarzania zajętej akcji.

Przykład kontroli gotowości

Trzech graczy, bez Norwegów: jedna plansza akcji stroną „3”, tor 7 rund, wyspy podstawowe wyłożone, wzgórze odkryte, talie pomocników osobno. Nikt nie siedzi z wikingami z przyszłych rund już w puli aktywnej.

Typowy błąd

Używać strony planszy dla innej liczby graczy albo mieszać żetony 4-graczy z planszami Norwegów.

Wyjątek i odsyłacz

Setup Norwegów zastępuje ten układ plansz — rozdział „Norwegowie”. Solo dodaje drugi zestaw wikingów — rozdział solo.

Podsumowanie i mikro-FAQ

Strona planszy = liczba graczy; wzgórza i wyspy na stole przed startem. P: Czy 4-gracze żetony idą z Norwegami? O: Nie. P: Czy tor rund można ustawić „na oko”? O: Nie — według wybranej długości.`
      },
      {
        id: 'player-setup',
        title: 'Przygotowanie gracza',
        requiredModuleIds: ['base'],
        keywords: ['wikingowie', 'plansza osady', 'stół biesiadny'],
        body: `Co to jest i po co

Każdy gracz buduje własny obszar: osadę, stół biesiadny i pulę wikingów. Jasny układ chroni przed użyciem wikinga z przyszłej rundy.

Co otrzymuje gracz

• planszę osady
• zestaw wikingów w swoim kolorze
• stół biesiadny
• początkowe zasoby, karty i elementy zgodnie z symbolem przygotowania
• elementy przeznaczone do wejścia w kolejnych rundach

Procedura

1. Połóż planszę osady przed sobą, stół biesiadny obok.
2. Ułóż wikingów tak, by było widać, którzy są dostępni, a którzy pojawią się później.
3. Odbierz elementy startowe wskazane symbolem przygotowania na planszy/komponencie.
4. Wydziel osobny obszar na zdobyte łodzie, budynki, zwierzęta i karty.

Przykład przy stole

Na starcie masz 2 aktywnych wikingów, a kolejni leżą na polach przyszłych rund. Nie wolno położyć „przyszłego” wikinga na polu akcji w rundzie 1.

Typowy błąd

Traktować wszystkich wikingów z pudełka jako od razu dostępnych albo mieszać własne kafle z wspólną pulą.

Wyjątek i odsyłacz

Dokładne ilości startowe odczytaj z symbolu przygotowania na komponencie (PARTIAL, jeśli symbol nieczytelny). Faza „nowy wiking”: rozdział 4.

Podsumowanie i mikro-FAQ

Aktywni wikingowie ≠ wszyscy wikingowie. P: Czy mogę użyć wikinga z przyszłej rundy? O: Nie. P: Gdzie trzymam zdobyte łodzie? O: W swoim obszarze, osobno od puli wspólnej.`
      },
    ],
  },
  {
    id: 'round',
    title: '4. Przebieg rundy',
    summary: 'Wszystkie fazy rundy w prawidłowej kolejności.',
    requiredModuleIds: ['base'],
    sections: [
      {
        id: 'round-overview',
        title: 'Kolejność faz',
        requiredModuleIds: ['base'],
        keywords: ['fazy', 'runda', 'kolejność', 'diagram'],
        body: `Co to jest i po co

Runda to stały zegar gry. Najwięcej decyzji przypada na fazę akcji, ale zaniedbanie biesiady, dochodu, premii i rozmnażania zwykle kosztuje więcej niż pojedyncza słabsza akcja.

Diagram faz (tekstowy)

1. Nowy wiking (jeśli tor przewiduje)
2. Żniwa (tylko w oznaczonych rundach)
3. Wzgórza — odwrócenie lub uzupełnienie zgodnie z torem
4. Broń — dobranie wskazanej liczby kart
5. Faza akcji
6. Ustalenie pierwszego gracza (osoba, która spasowała jako pierwsza w fazie akcji)
7. Dochód
8. Rozmnażanie zwierząt
9. Biesiada
10. Premie plansz
11. Porządki końca rundy / aktualizacja wzgórz według toru

Ikony na torze rund są rozstrzygające. Nie każda faza przynosi zasób w każdej rundzie. Przesuwaj znacznik fazy, aby nie pominąć dochodu, rozmnażania albo premii.

Przykład przy stole

Runda bez ikony żniw: pomijacie żniwa, ale nadal robicie dochód, rozmnażanie, biesiadę i premie, jeśli te fazy występują w strukturze rundy.

Typowy błąd

Po fazie akcji zbierać stół i zaczynać następną rundę bez dochodu i biesiady.

Wyjątek i odsyłacz

Szczegóły każdej fazy poniżej w tym rozdziale. Solo zmienia tylko blokowanie pól, nie kolejność faz.

Podsumowanie i mikro-FAQ

Akcja ≠ cała runda. P: Czy żniwa są co rundę? O: Nie — tylko gdy tor to pokazuje. P: Czy mogę zmienić kolejność faz „dla wygody”? O: Nie.`
      },
      {
        id: 'new-viking-and-harvest',
        title: 'Nowy wiking i żniwa',
        requiredModuleIds: ['base'],
        keywords: ['nowy wiking', 'żniwa', 'plony'],
        body: `Nowy wiking

Na początku rundy każdy gracz otrzymuje kolejnego wikinga, jeśli tor rundy to przewiduje. Od tej chwili wiking jest dostępny w fazie akcji.

Żniwa

Żniwa odbywają się tylko w oznaczonych rundach. Każdy gracz otrzymuje wskazane pomarańczowe dobra. Zebrana żywność może zostać wykorzystana do biesiady, handlu, ulepszania albo efektów kart. Nie pobieraj wszystkich rodzajów żywności automatycznie — tor rund pokazuje dokładny zestaw.

Przykład przy stole

Tor pokazuje żniwa: zboże. Bierzesz wskazany kafel zboża, nie „po jednym z każdego pomarańczowego”.

Typowy błąd

Pobrać żniwa w rundzie bez ikony albo wziąć zły rodzaj żywności.

Wyjątek i odsyłacz

Dokładne symbole żniw: tor rund / komponent. Faza akcji: następna sekcja.

Podsumowanie i mikro-FAQ

Nowy wiking według toru; żniwa tylko gdy ikona. P: Czy żniwa idą do wspólnej puli? O: Nie — do gracza. P: Czy mogę odmówić żniw? O: Zwykle bierzesz to, co wskazuje tor.`
      },
      {
        id: 'weapons-and-hills',
        title: 'Broń i wzgórza',
        requiredModuleIds: ['base'],
        keywords: ['karta broni', 'wzgórze', 'góry', 'zasoby'],
        body: `Broń

W fazie broni każdy dobiera wskazaną liczbę kart z talii broni. Karty przedstawiają narzędzia potrzebne w próbach łowieckich, wielorybnictwie i działaniach militarnych.

Wzgórza

Żeton wzgórz udostępnia materiały w kolejności przedstawionej na pasku. Gdy akcja pozwala pobrać zasoby ze wzgórza, bierzesz odpowiednią liczbę kolejnych dostępnych elementów od wskazanej strony. Nie wybierasz dowolnych symboli ze środka. Puste lub zużyte wzgórze jest odnawiane zgodnie z fazą i ikonami toru.

Przykład przy stole

Dobierasz 1 kartę broni. Później przy polowaniu odrzucasz tę kartę, by podnieść wynik kości — tylko jeśli pole polowania pokazuje ten typ broni.

Typowy błąd

Traktować każdą kartę broni jako uniwersalny +1 do każdego rzutu.

Wyjątek i odsyłacz

Modyfikatory zawsze z ikony konkretnego pola. Góry jako akcja: rozdział „Grupy akcji”.

Podsumowanie i mikro-FAQ

Broń z talii; wzgórza w kolejności. P: Czy mogę zatrzymać broń między rundami? O: Tak, o ile reguły/efekt nie każą jej odrzucić. P: Czy odnawiam wzgórze w środku fazy akcji? O: Tylko gdy tor/faza na to wskazuje.`
      },
      {
        id: 'action-phase',
        title: 'Faza akcji',
        requiredModuleIds: ['base'],
        keywords: ['akcje', 'kolumny', 'wikingowie', 'pas', 'pierwszy gracz'],
        body: `Co to jest i po co

Faza akcji to główne miejsce decyzji. Kolumna określa koszt w wikingach, a nie liczbę osobnych akcji.

Kiedy i kto

Gracze wykonują po jednej turze zgodnie z ruchem wskazówek zegara, począwszy od pierwszego gracza, aż wszyscy spasują albo nie mają dostępnych wikingów.

Procedura tury

1. Wybierz jedno wolne pole akcji.
2. Umieść na nim dokładnie tylu wikingów, ile wymaga kolumna.
3. Wykonaj akcję od lewej do prawej, zgodnie z ikonami pola.
4. Albo spasuj — po spasowaniu nie wracasz już do fazy akcji w tej rundzie.

Pierwszy gracz na następną rundę

Gracz, który spasuje jako pierwszy w fazie akcji, otrzymuje znacznik pierwszego gracza i będzie zaczynał fazę akcji w następnej rundzie. Pozostali kontynuują, aż wszyscy spasują albo nie będą mieli wikingów. Nie czekaj z oddaniem znacznika do końca rundy — przekazujesz go w momencie pierwszego pasu.

Zajęte pola

Zwykle nie wolno wybrać zajętego pola. Jeżeli wariant dla 4 graczy pozwala powtórzyć cudzą akcję za pomocą specjalnego elementu, stosuje się wyłącznie warunki tego wariantu. Dodatek Norwegowie usuwa tę możliwość.

Przykład przy stole

Pole w trzeciej kolumnie wymaga jednoczesnego położenia trzech dostępnych wikingów. Masz tylko dwóch — nie możesz wybrać tego pola „za dwóch”. Ania spasuje pierwsza przy dwójce wikingów w zapasie: bierze znacznik pierwszego gracza, a Bartek i Celina jeszcze wykonują swoje akcje.

Typowy błąd

Kłaść wikingów „na raty”, wracać do akcji po spasowaniu albo przekazywać znacznik pierwszego gracza osobie, która spasowała jako ostatnia.

Wyjątek i odsyłacz

Piąta kolumna Norwegów kończy twój udział po jej użyciu — rozdział „Norwegowie”. Solo: blokowanie pól z poprzedniej rundy.

Podsumowanie i mikro-FAQ

Kolumna = koszt wikingów; pas = koniec twoich akcji; pierwszy pas = znacznik pierwszego gracza. P: Czy mogę zająć zajęte pole? O: Zwykle nie. P: Czy częściowy koszt wystarczy? O: Nie. P: Kto zaczyna następną rundę? O: Ten, kto spasował jako pierwszy.`
      },
      {
        id: 'income',
        title: 'Dochód',
        requiredModuleIds: ['base'],
        keywords: ['dochód', 'linia dochodu', 'srebro'],
        body: `Co to jest i po co

Dochód to srebro z własnych plansz. Budujesz je układem kafli, nie jednorazową akcją.

Procedura

1. Dla każdej własnej planszy (osada, wyspa, wskazane budynki) znajdź linię dochodu.
2. Otrzymujesz wartość najwyższego widocznego pola dochodu, do którego masz nieprzerwane prawo zgodnie z układem zakrytych pól.
3. Zsumuj srebro ze wszystkich swoich plansz.

Kluczowa zasada

Nie zakrywaj symbolu dochodu, który chcesz pobierać. To pole powinno pozostać widoczne, podczas gdy wymagany obszar poniżej lub obok niego musi być prawidłowo wypełniony. Pojedyncza luka może zatrzymać linię, nawet jeśli dalsze pola są zakryte.

Diagram obudowania (tekstowy)

Legalne: ciąg zakrytych pól prowadzi do odkrytego symbolu „2”; bierzesz 2 srebra z tej planszy.
Legalne: ciąg prowadzi dalej do odkrytego „3”; bierzesz 3, nie sumujesz 2+3.
Nielegalne / nieskuteczne: symbol „3” zakryty kaflami — ten próg nie wypłaca.
Nielegalne / nieskuteczne: między startem linii a symbolem jest pusta kratka — ciąg się urywa; obowiązuje niższy nieprzerwany próg albo brak wypłaty z tej linii.

Przykład przy stole

Zakryłeś pola prowadzące do dochodu 2, ale między nimi jest dziura. Linia się urywa — bierzesz tylko niższy aktywny próg albo nic z tej planszy, zgodnie z układem.

Typowy błąd

Zakryć sam symbol dochodu „żeby było czysto” albo liczyć dochód z cudzych plansz.

Wyjątek i odsyłacz

Układanie: rozdział 5. Premie są osobną fazą — nie myl obudowania dochodu z otoczeniem premii.

Podsumowanie i mikro-FAQ

Widoczny symbol + ciągłość zakrycia. P: Czy zakrywam pole dochodu, by je dostać? O: Nie. P: Czy każda plansza liczy się osobno? O: Tak.`
      },
      {
        id: 'breeding',
        title: 'Rozmnażanie zwierząt',
        requiredModuleIds: ['base'],
        keywords: ['rozmnażanie', 'owca', 'krowa', 'ciężarna', 'zwierzęta'],
        body: `Co to jest i po co

Owce i krowy mają stronę zwykłą oraz ciężarną. Rozmnażanie buduje stado bez osobnej akcji — ale tylko raz na rodzaj w fazie.

Procedura dla każdego rodzaju osobno

1. Jeśli masz co najmniej dwie sztuki danego rodzaju i żadna nie jest ciężarna, odwróć jedną na stronę ciężarną.
2. Jeśli masz ciężarne zwierzę danego rodzaju, odwróć je na stronę zwykłą i otrzymaj nowe zwykłe zwierzę tego rodzaju.

Diagram rozmnażania (tekstowy)

Runda N: 2 owce zwykłe → odwróć 1 na ciężarną (krok 1). Stop.
Runda N+1: masz ciężarną → odwróć na zwykłą i weź 1 nową owcę (krok 2). Masz teraz 3 zwykłe.
Runda N+1 (ten sam rodzaj): nie wykonujesz od razu kroku 1 na tych samych owcach w tej samej fazie — jeden krok na rodzaj na fazę.
Osobno: krowy liczą się własnym łańcuchem; nie łącz owiec z krowami.

Ograniczenie

Nie można w tej samej fazie najpierw stworzyć ciężarnej sztuki, a następnie natychmiast urodzić z niej młodego.

Przykład przy stole

Masz 2 owce zwykłe. W fazie odwracasz jedną na ciężarną. Nie dostajesz jeszcze trzeciej owcy w tej samej fazie. Dopiero w następnej fazie rozmnażania ciężarna rodzi.

Typowy błąd

„Podwójne” rozmnażanie w jednej fazie albo mieszanie owiec z krowami w jednym liczeniu.

Wyjątek i odsyłacz

Norwegowie: klacze jak owce/krowy; świnie bez strony ciężarnej — rozdział „Norwegowie”.

Podsumowanie i mikro-FAQ

Jeden krok na rodzaj na fazę. P: Czy 1 owca wystarczy? O: Nie do rozpoczęcia ciąży. P: Czy mogę w tej samej fazie zajść w ciążę i urodzić? O: Nie.`
      },
      {
        id: 'feast',
        title: 'Biesiada',
        requiredModuleIds: ['base'],
        keywords: ['biesiada', 'stół', 'żywność', 'kara', 'uczta'],
        body: `Co to jest i po co

Na koniec rundy musisz zapełnić stół biesiadny. To obowiązkowy koszt utrzymania wikingów, nie opcjonalny scoring.

Kiedy

Po rozmnażaniu, przed premiami (zgodnie z kolejnością faz).

Procedura

1. Sprawdź dostępną długość stołu — zależy od liczby aktywnych wikingów i rundy.
2. Zakryj wymagane segmenty odpowiednimi pomarańczowymi i czerwonymi dobrami oraz srebrem.
3. Przestrzegaj zasad sąsiedztwa na stole: dobra tego samego rodzaju lub koloru nie mogą tworzyć niedozwolonego bezpośredniego sąsiedztwa wskazanego na stole.
4. Kafla nie wolno obracać na drugą stronę bez efektu ulepszenia.
5. Srebro może wypełniać pojedyncze luki.
6. Za każde niezakryte pole otrzymaj karę zgodną z planszą.
7. Dobra wykorzystane do biesiady wracają do zasobów ogólnych — nie zostają na stole na następną rundę.

Diagram biesiady (tekstowy)

1. Odczytaj długość stołu z planszy / liczby wikingów.
2. Ułóż legalny rząd żywności i srebra na wymaganych polach.
3. Policz niezakryte pola → zastosuj karę z planszy za każde.
4. Zwróć użyte kafle i monety do zasobów.
5. Żetony emigracji / małej emigracji na stole pozostają zgodnie z ich regułą — nie są „żywnością do zwrotu”.

Przykład przy stole

Stół wymaga 4 pól. Kładziesz 2 pomarańczowe kafle i 2 srebra w legalnym układzie. Po rozliczeniu kafle wracają do pudła, srebro do banku; kara nie występuje.

Typowy błąd

Zostawiać żywność na stole „na następną rundę” albo ignorować karę za luki.

Wyjątek i odsyłacz

Żetony emigracji mogą ułatwiać biesiadę — rozdział o emigracji. Norwegowie: mała emigracja kładzie żeton na stole.

Podsumowanie i mikro-FAQ

Zakryj stół albo płać karę; żywność wraca do zasobów. P: Czy zielone dobra karmią stół? O: Nie — używasz pomarańczowych/czerwonych i srebra zgodnie ze stołem. P: Czy mogę odwrócić kafel przy kładzeniu na stół? O: Nie bez ulepszenia.`
      },
      {
        id: 'bonuses',
        title: 'Premie plansz',
        requiredModuleIds: ['base'],
        keywords: ['premia', 'otoczenie', 'bonus'],
        body: `Co to jest i po co

Pole premii nagradza staranne otoczenie symbolu kaflami. To powtarzalny dochód w dobrach, nie jednorazowy punkt.

Procedura

1. Sprawdź każde pole premii na swoich planszach.
2. Premia działa, gdy wszystkie wymagane pola otaczające symbol są zakryte, a sam symbol pozostaje widoczny.
3. W fazie premii otrzymujesz przedstawione dobro lub zasób z każdej aktywnej premii.

Timing

Premia może uruchamiać się ponownie w każdej rundzie, o ile jej warunki nadal są spełnione. Nie pobierasz jej natychmiast po otoczeniu, chyba że konkretny efekt wyraźnie tak stanowi.

Przykład przy stole

Otoczyłeś premię w trakcie fazy akcji. Czekasz do fazy premii i dopiero wtedy bierzesz wskazany kafel.

Typowy błąd

Brać premię od razu po położeniu ostatniego kafla albo zakryć sam symbol premii.

Wyjątek i odsyłacz

Dochód ≠ premia. Układanie wokół symbolu: rozdział 5.

Podsumowanie i mikro-FAQ

Otoczony + odkryty symbol; wypłata w fazie premii. P: Czy premia jest jednorazowa? O: Nie, co rundę gdy warunek stoi. P: Czy mogę zakryć symbol premii? O: Wtedy przestaje działać.`
      },
    ],
  },
  {
    id: 'placing-goods',
    title: '5. Układanie dóbr na planszach',
    summary: 'Zasady kolorów, sąsiedztwa, srebra, premii i zakrywania pól.',
    requiredModuleIds: ['base'],
    sections: [
      {
        id: 'placement-basics',
        title: 'Zasady podstawowe',
        requiredModuleIds: ['base'],
        keywords: ['układanie', 'kafle', 'obracanie', 'nakładanie', 'sąsiedztwo'],
        body: `Co to jest i po co

Układanie zamienia zdobyte kafle w ochronę przed karami, dochód i premie. Legalność położenia jest ważniejsza niż „ładny” kształt.

Procedura położenia

1. Kafel musi mieścić się w całości na dozwolonych polach siatki.
2. Nie może nachodzić na inny kafel, symbol, ramkę ani obszar wyłączony z układania.
3. Możesz obracać kafel przestrzennie o 90 lub 180 stopni.
4. Nie wolno odwrócić go na drugą stronę bez ulepszenia.
5. Położonego kafla zasadniczo nie przesuwa się później, chyba że efekt wyraźnie na to pozwala.

Sąsiedztwo kolorów

Dobra czerwone i pomarańczowe nie mogą stykać się bokami z innymi czerwonymi lub pomarańczowymi dobrami na planszach osady, wysp i budynków, chyba że konkretna plansza lub efekt stanowi inaczej. Mogą stykać się narożnikami. Zielone i niebieskie dobra nie mają tego ograniczenia.

Srebro

Srebro może wypełniać pojedyncze pola i jest szczególnie przydatne do rozdzielania żywności albo domykania linii dochodu.

Diagram legalności (tekstowy)

Legalne: dwa czerwone stykają się tylko rogiem; srebro między dwoma pomarańczowymi.
Nielegalne: dwa pomarańczowe dzielą pełny bok; kafel nachodzi na ramkę; odwrócenie na drugą stronę „bo tak lepiej pasuje”.

Przykład przy stole

Chcesz położyć dwa czerwone kafle obok siebie bokami — to niedozwolone. Wstawiasz między nie monetę albo zmieniasz układ.

Typowy błąd

Odwracać kafel bez ulepszenia albo przesuwać stare kafle, by „poprawić” linię dochodu.

Wyjątek i odsyłacz

Biesiada ma własne reguły sąsiedztwa na stole. Szopy rzemieślnicze: rozdział Norwegowie.

Podsumowanie i mikro-FAQ

Obrót OK, odwrócenie nie; czerwone/pomarańczowe bez styku bokiem. P: Czy styk rogiem jest OK? O: Tak. P: Czy mogę później przesunąć kafel? O: Zasadniczo nie.`
      },
      {
        id: 'negative-points',
        title: 'Pola ujemnych punktów',
        requiredModuleIds: ['base'],
        keywords: ['minus', 'ujemne punkty', 'zakrycie'],
        body: `Co to jest i po co

Każde widoczne pole z wartością ujemną obniża wynik na koniec gry. Zakrycie pola usuwa tę karę. Nie zdobywasz punktów za samo zakrycie pola — po prostu nie tracisz wskazanej wartości.

Strategia

W praktyce najpierw warto budować stabilną linię dochodu i premie, a dopiero potem wypełniać odległe obszary. Chaotyczne rozrzucanie małych kafli często blokuje większe kształty i zostawia kosztowne luki.

Przykład przy stole

Masz odkryte −8 na brzegu i −2 w środku. Duży niebieski kafel zakrywa −8. Małe −2 zostawiasz na później zamiast psuć linię dochodu.

Typowy błąd

Liczyć „+8 za zakrycie” zamiast „już nie −8”.

Wyjątek i odsyłacz

Punktacja końcowa: rozdział 7. Wszystkie widoczne minusy na osadzie, wyspach i budynkach się sumują.

Podsumowanie i mikro-FAQ

Zakrycie = brak kary, nie bonus. P: Czy zakryte minusy liczą się na koniec? O: Nie. P: Czy muszę zakryć wszystko? O: Nie, ale każde widoczne odejmujesz.`
      },
      {
        id: 'bonus-and-income-pattern',
        title: 'Jak łączyć dochód z premiami',
        requiredModuleIds: ['base'],
        keywords: ['strategia', 'linia', 'premia'],
        body: `Co to jest i po co

Dobre układanie zwykle przebiega od zwartego narożnika w kierunku kolejnych wartości dochodu. Pozostawiasz widoczny symbol dochodu, ale zakrywasz pola wymagane do jego aktywacji. Wokół premii planujesz pierścień kafli, pozostawiając środek odkryty.

Przed położeniem dużego kafla sprawdź

1. Czy nie zakryje potrzebnej premii
2. Czy nie odetnie linii dochodu
3. Czy nie stworzy jednopunktowej szczeliny, której nie da się łatwo wypełnić
4. Czy pozostawi miejsce na następny duży kafel

Przykład przy stole

Duży zielony kafel idealnie zasłania −6, ale nachodzi na symbol premii. Obracasz go inaczej albo bierzesz mniejszy kafel i zostawiasz premię żywą.

Typowy błąd

„Dokładać cokolwiek”, byle zakryć minus, niszcząc przy tym dochód wart więcej w długim terminie.

Wyjątek i odsyłacz

Fazy wypłaty: rozdział 4. Legalność styku: sekcja powyżej.

Podsumowanie i mikro-FAQ

Dochód i premia wymagają widocznych symboli. P: Czy mogę poświęcić premię dla minusu? O: Tak, to decyzja — świadoma. P: Czy srebro pomaga w lukach? O: Tak, pojedyncze pola.`
      },
    ],
  },
  {
    id: 'action-groups',
    title: '6. Grupy akcji',
    summary: 'Co oznaczają najważniejsze rodziny pól akcji.',
    requiredModuleIds: ['base'],
    sections: [
      {
        id: 'production-and-market',
        title: 'Produkcja, targ i wymiana',
        requiredModuleIds: ['base'],
        keywords: ['produkcja', 'targ', 'wymiana', 'ulepszenie'],
        body: `Co to jest i po co

Akcje produkcyjne i targowe zasilają zapas dóbr, materiałów i broni. Są fundamentem biesiady i budowy.

Procedura

1. Wybierz wolne pole i opłać koszt kolumny wikingami.
2. Pobierz dokładnie elementy wskazane na polu.
3. Gdy pole pozwala wybrać jeden z kilku symboli, dokonaj wyboru w chwili wykonania akcji.
4. Przy targu i wymianie opłać cały obowiązkowy koszt przed wzięciem korzyści.
5. Przy ulepszaniu odwróć posiadane kafle — nie bierz odpowiedników z zasobów.

Przykład przy stole

Pole pokazuje „zapłać 1 srebro, weź 2 pomarańczowe”. Nie masz srebra — nie wybierasz tego pola „tylko po nagrodę”.

Typowy błąd

Wykonać tylko korzystną część połączonego efektu albo ulepszać przez wymianę na inny kafel z pudła.

Wyjątek i odsyłacz

Ikony i koszty konkretnych pól: plansza akcji (PARTIAL). Norweski targ zwierzęcy: rozdział Norwegowie.

Podsumowanie i mikro-FAQ

Cały obowiązkowy efekt albo nic. P: Czy „możesz” oznacza opcjonalność? O: Tak — w przeciwieństwie do połączonego kosztu. P: Czy ulepszenie bierze nowy kafel? O: Nie — odwraca posiadany.`
      },
      {
        id: 'mountains-and-snares',
        title: 'Góry i zastawianie pułapek',
        requiredModuleIds: ['base'],
        keywords: ['góry', 'wzgórza', 'pułapki', 'zastawianie', 'łowy'],
        body: `Góry

Akcje gór / wzgórz pozwalają pobrać materiały z paska wzgórz. Bierzesz kolejną wskazaną liczbę zasobów od właściwej strony paska — bez wybierania ze środka. To główne źródło drewna, kamienia i rudy poza innymi polami produkcyjnymi.

Zastawianie pułapek

Zastawianie pułapek należy do rodziny prób łowieckich. Wykonujesz próbę zgodnie z ikonami konkretnego pola: kość, wymagany typ broni i ewentualne dozwolone modyfikatory. Sukces daje wskazane dobra lub zwierzęta; porażka — rekompensatę pokazaną na polu, zwykle karty broni lub zasoby.

Procedura próby (ogólna)

1. Potwierdź, że masz wymagania pola (broń, zasoby, statek — jeśli wskazane).
2. Rzuć wskazaną kością.
3. Modyfikuj wynik wyłącznie legalnymi kartami/zasobami z ikony pola.
4. Przy sukcesie weź nagrodę z pola; przy porażce weź rekompensatę z pola.

Przykład przy stole

Pole pułapek wymaga określonej broni. Masz tylko inny typ karty — nie wolno jej użyć „bo też jest bronią”. Albo dobierasz właściwą broń wcześniej, albo wybierasz inne pole.

Typowy błąd

Traktować pułapki jak zwykłe polowanie z tymi samymi modyfikatorami albo brać dowolny zasób ze środka wzgórza.

Wyjątek i odsyłacz

Dokładne progi sukcesu i nagrody: ikona pola (PARTIAL). Polowanie i wielorybnictwo: sekcje poniżej. Norweskie łowy (ryby, łoś): bez drewna jako modyfikatora.

Podsumowanie i mikro-FAQ

Góry = kolejność z paska; pułapki = próba według pola. P: Czy mogę wybrać rudę ze środka? O: Nie. P: Czy każda broń pasuje do pułapek? O: Tylko typ z ikony.`
      },
      {
        id: 'building',
        title: 'Budowa łodzi i budynków',
        requiredModuleIds: ['base'],
        keywords: ['budowa', 'szopa', 'kamienny dom', 'długi dom'],
        body: `Co to jest i po co

Budowa zamienia materiały w łodzie (dostęp do akcji) albo budynki (nowe plansze do zakrywania i punkty).

Procedura

1. Wybierz pole budowy.
2. Opłać wskazany koszt materiałów.
3. Weź element z zasobów — od tej chwili należy do ciebie.
4. Budynki połóż w swoim obszarze jako dodatkową planszę przestrzenną.

Uwagi

Budynki mają własne pola ujemne, możliwe premie, miejsca specjalne i wartość punktową. Przed budową upewnij się, że masz dobra odpowiednich kolorów i kształtów, aby budynek nie stał się wyłącznie źródłem kary.

Łódź może być później wykorzystana wiele razy, o ile akcja jej nie usuwa. Emigracja usuwa statek na stałe.

Przykład przy stole

Budujesz szopę za drewno. Nie masz jeszcze kafli na jej minusy — odkładasz budowę o rundę albo bierzesz tańszy statek, którego potrzebujesz do akcji.

Typowy błąd

Budować „dla punktów na kaflu” bez planu zakrycia minusów.

Wyjątek i odsyłacz

Szopa rzemieślnicza Norwegów: rozdział Norwegowie. Koszty z ikon planszy (PARTIAL).

Podsumowanie i mikro-FAQ

Zapłać → weź element. P: Czy łódź znika po użyciu? O: Nie, chyba że emigracja/efekt. P: Czy budynek od razu punktuje pełną wartość? O: Punkty z notesu; minusy obniżają, dopóki widoczne.`
      },
      {
        id: 'hunting',
        title: 'Polowanie',
        requiredModuleIds: ['base'],
        keywords: ['polowanie', 'kość', 'łuk', 'drewno', 'porażka'],
        body: `Co to jest i po co

Polowanie to próba kością o dobra. Ryzyko jest świadome: porażka nie musi oznaczać pustki — pole pokazuje rekompensatę.

Procedura

1. Spełnij wymagania pola (typ broni, ewentualnie statek/zasoby).
2. Rzuć wskazaną kością.
3. Modyfikuj wynik, odrzucając dozwolone karty broni i — gdy pole na to pozwala — drewno.
4. Sukces: weź pokazane dobra.
5. Porażka: weź rekompensatę z pola; zanim potwierdzisz porażkę, możesz wydać kolejne legalne modyfikatory.

Diagram testu kością i broni (tekstowy)

1. Potwierdź wymagania pola (np. łuk).
2. Rzut kością = wynik bazowy.
3. Odrzuć kartę broni typu z ikony → podnieś wynik o wartość karty / efektu.
4. Jeśli pole pokazuje drewno jako modyfikator, możesz je wydać; jeśli nie — drewno nie działa.
5. Porównaj wynik z progiem sukcesu na polu.
6. Sukces → nagroda z pola. Porażka → rekompensata z pola (często broń lub zasoby).

Przykład przy stole

Rzut 3, potrzebujesz wyższego progu. Odrzucasz kartę łuku pokazaną na polu i podnosisz wynik. Drewna nie wydajesz, bo to pole go nie pokazuje.

Typowy błąd

Używać niewskazanych kart broni „bo tematycznie pasują” albo poddawać się bez sprawdzenia rekompensaty.

Wyjątek i odsyłacz

Pułapki i wielorybnictwo mają własne pola. Norweskie ryby/łoś: bez drewna.

Podsumowanie i mikro-FAQ

Tylko modyfikatory z ikony. P: Czy porażka = nic? O: Nie — zwykle jest rekompensata. P: Czy drewno zawsze działa? O: Nie.`
      },
      {
        id: 'whaling',
        title: 'Wielorybnictwo',
        requiredModuleIds: ['base'],
        keywords: ['wielorybnictwo', 'wielorybnik', 'mięso wieloryba'],
        body: `Co to jest i po co

Wielorybnictwo daje duże kafle mięsa i inne korzyści, ale wymaga wielorybnika oraz przygotowania broni.

Procedura

1. Upewnij się, że masz dostępnego wielorybnika (chyba że pole pozwala go zbudować w ramach tej samej akcji).
2. Wykonaj próbę zgodnie z polem: kości, broń, dozwolone modyfikatory.
3. Posiadane wielorybniki mogą wpływać na koszt lub siłę próby dokładnie w sposób pokazany ikonami.
4. Po sukcesie weź zestaw nagród z pola.

Przykład przy stole

Masz wielorybnika i właściwą broń. Sukces daje duże kafle mięsa — planujesz od razu, gdzie je położysz, by nie złamać sąsiedztwa czerwonego.

Typowy błąd

Iść na wielorybnictwo bez statku albo bez miejsca na duże kafle.

Wyjątek i odsyłacz

Mała emigracja zużywa wielorybnika — Norwegowie. Ikony pól: PARTIAL.

Podsumowanie i mikro-FAQ

Najpierw statek, potem próba. P: Czy knara zastąpi wielorybnika? O: Nie. P: Czy porażka coś daje? O: Zgodnie z rekompensatą na polu.`
      },
      {
        id: 'raiding-and-pillaging',
        title: 'Najazd i plądrowanie',
        requiredModuleIds: ['base'],
        keywords: ['najazd', 'plądrowanie', 'miecz', 'langskip', 'żeton specjalny', 'rabunek'],
        body: `Co to jest i po co

Akcje militarne zdobywają żetony specjalne o nieregularnych kształtach — świetne do domykania plansz.

Procedura

1. Spełnij wymaganie statku (zwykle langskip).
2. Rzuć kością.
3. Zwiększ wynik wyłącznie legalnymi modyfikatorami z pola: kartami broni, rudą na statku albo innymi dozwolonymi zasobami.
4. Po udanej próbie wybierz żeton specjalny, którego wartość miecza nie przekracza osiągniętego wyniku. Nie musisz brać żetonu o wartości dokładnie równej wynikowi.

Nazwy akcji

Najazd, plądrowanie, rabunek i grabież są osobnymi nazwami akcji. Efekt odnoszący się do jednej z nich nie obejmuje automatycznie pozostałych.

Przykład przy stole

Wynik końcowy 7. Bierzesz dostępny żeton o wartości miecza 6 — legalnie. Nie musisz szukać dokładnie „7”.

Typowy błąd

Brać żeton droższy niż wynik albo stosować kartę „na Najazd” do Grabieży.

Wyjątek i odsyłacz

Grabież (knara, bez kamieni): Norwegowie. Rzemiosło kowalskie bierze żeton bez rzutu — Norwegowie.

Podsumowanie i mikro-FAQ

Wynik ≥ wartość miecza żetonu. P: Czy muszę wziąć dokładnie wynik? O: Nie — równy lub niższy. P: Czy nazwy militarne są zamienne? O: Nie.`
      },
      {
        id: 'exploration',
        title: 'Odkrywanie wysp',
        requiredModuleIds: ['base'],
        keywords: ['wyspa', 'wyprawa', 'odkrywanie'],
        body: `Co to jest i po co

Wyprawa dodaje nową planszę przestrzenną: dochód, premie i punkty, ale też nowe minusy.

Procedura

1. Sprawdź, jaki statek jest wymagany i które wyspy pole pozwala odkryć.
2. Wybierz jedną dostępną planszę wyspy.
3. Weź ją do swojego obszaru.
4. Od tej chwili traktuj ją jak kolejną własną planszę przy układaniu, dochodzie, premiach i punktacji.

Przykład przy stole

Odkrywasz wyspę, gdy masz już kilka kafli pasujących do jej układu albo widzisz premię, którą szybko uruchomisz. Sama wyspa bez planu to często netto strata z minusów.

Typowy błąd

Brać wyspę „bo jest” bez zasobów na zakrycie.

Wyjątek i odsyłacz

Norwegowie zmieniają zestaw wysp — rozdział Norwegowie. Wymagany statek: ikona pola.

Podsumowanie i mikro-FAQ

Wyspa = nowa plansza z plusami i minusami. P: Czy mogę odkryć kilka naraz? O: Tylko jeśli pole na to pozwala. P: Czy dochód z wyspy liczy się od razu? O: Od fazy dochodu, gdy linia jest aktywna.`
      },
      {
        id: 'emigration',
        title: 'Emigracja',
        requiredModuleIds: ['base'],
        keywords: ['emigracja', 'statek', 'biesiada', 'punkty'],
        body: `Co to jest i po co

Emigracja zamienia łódź na trwałe punkty i ułatwienie biesiady. To inwestycja późniejszej gry, gdy statek wykonał już kluczowe zadania.

Procedura

1. Wybierz pole emigracji i opłać koszt kolumny.
2. Usuń wskazaną łódź z twojego obszaru.
3. Weź żeton emigracji / punkty oraz trwałe ułatwienie biesiady pokazane na żetonie lub stole.
4. Ruda na emigrującej łodzi przepada, o ile efekt nie stanowi inaczej.

Diagram emigracji (tekstowy)

Przed: masz langskip z rudą + potrzebujesz ułatwienia stołu.
Akcja: opłać kolumnę → usuń langskip (i rudę na nim) → weź żeton emigracji.
Po: żeton pomaga w biesiadach; langskipa nie użyjesz już do najazdów ani wypraw.
Odróżnij: mała emigracja (Norwegowie) usuwa wielorybnik i kładzie inny żeton na stole — to nie ta sama akcja.

Przykład przy stole

Langskip zrobił już najazdy. Emigrujesz go w rundzie 5, bierzesz żeton ułatwiający stół i punkty. Rudy na pokładzie nie odzyskujesz.

Typowy błąd

Emigrować jedyny statek potrzebny do jeszcze zaplanowanej wyprawy albo mylić emigrację z małą emigracją Norwegów.

Wyjątek i odsyłacz

Mała emigracja (wielorybnik) jest osobna — Norwegowie. Karty mówiące „emigracja” nie obejmują automatycznie małej emigracji.

Podsumowanie i mikro-FAQ

Statek znika; korzyść zostaje. P: Czy mogę użyć emigrowanej łodzi później? O: Nie. P: Co z rudą? O: Zwykle przepada.`
      },
    ],
  },
  {
    id: 'endgame',
    title: '7. Koniec gry i punktacja',
    summary: 'Pełna procedura końca gry.',
    requiredModuleIds: ['base'],
    sections: [
      {
        id: 'final-scoring',
        title: 'Kolejność punktowania',
        requiredModuleIds: ['base'],
        keywords: ['punktacja', 'koniec gry', 'wynik', 'remis'],
        body: `Kiedy

Po zakończeniu ostatniej rundy — po wszystkich jej fazach — wykonaj pełną punktację według notesu.

Procedura

1. Zsumuj dodatnie wartości kategorii notesu, w tym przede wszystkim: łodzie, emigrację, wyspy i odkrycia, budynki, zwierzęta, karty pomocników zapewniające punkty, srebro i inne elementy wskazane w arkuszu, dodatkowe żetony punktów.
2. Odejmij wszystkie widoczne ujemne pola na planszy osady, wyspach i budynkach oraz inne kary.
3. Każdy element policz tylko w jednej właściwej kategorii.
4. Wygrywa najwyższy wynik.

Uwagi

Kafel dobra leżący na planszy zwykle nie daje osobno swojej wartości — jego znaczenie polega na zakryciu kary, uruchomieniu dochodu albo premii.

Remis

Remis rozstrzygnij zgodnie z oficjalnym kryterium używanym przez grę; jeśli aplikacja nie przechowuje tego kryterium, pozostaw graczy na równorzędnym miejscu zamiast wymyślać nowe. [DO WERYFIKACJI przy stole z notesem/wydaniem]

Przykład przy stole

Notes: łodzie 10, wyspa 8, budynki 12, zwierzęta 6, srebro 4, emigracja 7 = 47. Widoczne minusy −9. Wynik 38.

Typowy błąd

Doliczać punkty z kafli dóbr „bo ładne” albo pomijać minusy na budynkach.

Wyjątek i odsyłacz

Norwegowie: żetony PZ; punkty nadrukowane na łodzi/zwierzęciu na szopie rzemieślniczej nie liczą się osobno. Solo: porównuj rekordy przy tej samej konfiguracji.

Podsumowanie i mikro-FAQ

Notes + minusy = wynik. P: Czy kafel na planszy to VP? O: Zwykle nie osobno. P: Czy aplikacja zna oficjalny tie-break? O: Nie gwarantuje — DO WERYFIKACJI.`
      },
    ],
  },
  {
    id: 'norwegians-setup',
    title: '8. Norwegowie',
    summary: 'Cały dodatek Norwegowie w jednym rozdziale — przygotowanie i zasady.',
    requiredModuleIds: ['base', 'norwegians'],
    sections: [
      {
        id: 'norwegians-main',
        title: 'Dodatek Norwegowie',
        requiredModuleIds: ['base', 'norwegians'],
        keywords: [
          'Norwegowie',
          'plansze akcji',
          '5 kolumna',
          'piąta kolumna',
          'nowe wyspy',
          'szopa rzemieślnicza',
          'klacz',
          'świnia',
          'zioła',
          'grabież',
          'mała emigracja',
          'żeton PZ',
          'targ zwierzęcy',
          'kleszcze',
          'ryby',
          'łoś',
        ],
        body: `Status dodatku: PARTIAL

Procedury systemowe Norwegów są opisane poniżej w jednym rozdziale. Dokładne ikony i koszty nowych pól oraz pełne teksty kart pomocników odczytuj z komponentów — nie zgaduj katalogu.

Co dodaje Norwegowie i po co

Dodatek wymienia plansze akcji, dokłada piątą kolumnę, nowe dobra i zwierzęta, szopy rzemieślnicze, grabież, małą emigrację, żetony PZ oraz zmieniony zestaw wysp. Cel gry pozostaje ten sam: najwyższy wynik po ostatniej rundzie.

Przygotowanie plansz akcji

Odłóż podstawową planszę akcji. Użyj trzech oddzielnych plansz akcji z dodatku, stroną odpowiadającą liczbie graczy. Nie używaj podstawowych żetonów rozszerzających dla 4 graczy. Z dodatkiem Norwegowie nie można powtarzać zajętej akcji innego gracza przy użyciu tych żetonów.

Wyspy

Cztery nowe wyspy z dodatku zastępują podstawowy zestaw i zawsze mogą być dostępne.

Przy 3 graczach dołóż dwie poprawione wyspy z gry podstawowej: jedną z grupy A/B oraz jedną z grupy C/D.

Przy 4 graczach dołóż wszystkie cztery poprawione wyspy z gry podstawowej.

Można używać większej liczby wysp lub wysp z minidodatków, ale jest to opcjonalne.

Szopy rzemieślnicze — przygotowanie

Potasuj sześć szop rzemieślniczych i rozdaj każdemu graczowi po jednej. Pozostałe odłóż. Rozdana szopa nie jest jeszcze zbudowana — leży obok obszaru gracza jako możliwość na później.

Gdy akcja pozwala wybudować zwykłą szopę, możesz zamiast niej wybudować swoją szopę rzemieślniczą, wybierając jedną z jej stron. Każdy gracz może zrobić to tylko raz w partii.

Nowe dobra i plansze organizacyjne

Dodaj nowe pudełko z dobrami i rozdziel kafle według obu stron. Przygotuj klacze, świnie, zioła, poroża, narzędzia, skóry, flausz, miód pitny, kamienie runiczne i pozostałe dobra dodatku.

Małe żetony specjalne oznaczone kleszczami kowalskimi oraz żetony o wartości miecza 8 lub mniejszej połóż na nowej owalnej planszy. Pozostałe żetony specjalne umieść na odwróconej, neutralnej stronie dużej planszy organizacyjnej.

Użyj nowej planszy na łodzie z miejscem na małą emigrację. Przygotuj żetony punktów zwycięstwa.

Piąta kolumna

Pole w piątej kolumnie przyjmuje 1 albo 2 wikingów zgodnie z oznaczeniem. Po wykonaniu tej akcji musisz zakończyć udział w fazie akcji: nie możesz później umieścić żadnych kolejnych wikingów w tej rundzie.

Jeżeli użyjesz dwóch wikingów, oprócz właściwej akcji możesz zagrać jedną kartę pomocnika. Niewykorzystani wikingowie nie wracają do akcji — wybór piątej kolumny oznacza definitywne spasowanie po tej turze.

Klacze i świnie

Klacze są czerwonymi dobrami i zwierzętami. Rozmnażają się tak jak owce i krowy, z użyciem strony ciężarnej.

Świnie są czerwonymi dobrami i zwierzętami, ale nie mają strony ciężarnej. Jeżeli podczas fazy rozmnażania masz co najmniej dwie świnie, otrzymujesz jedną nową świnię w każdej rundzie.

Klacze i świnie możesz trzymać w oborach razem z innymi zwierzętami albo obok planszy osady.

Łowienie ryb i polowanie na łosia

Nowe próby łowieckie mają własne ikony broni. Wynik możesz modyfikować wyłącznie odpowiednimi kartami broni. Drewno nie jest legalnym modyfikatorem tych dwóch akcji. Po porażce otrzymujesz broń przedstawioną na polu.

Targ zwierzęcy

Pola targu zwierzęcego pozwalają otrzymać wskazaną liczbę zwierząt i wybrać ich kombinację spośród dozwolonych rodzajów. Jeżeli pole pokazuje owce i świnie, możesz wziąć dwie owce, dwie świnie albo po jednej. Oprócz srebra trzeba zapłacić pokazany kafel dobra. Koszt kafla nie jest opcjonalny.

Rzemiosło kowalskie

Nowa akcja rzemiosła pozwala odłożyć jedną rudę i otrzymać jeden dostępny żeton specjalny oznaczony kleszczami kowalskimi, którego wartość miecza jest niższa niż 9. Nie wykonujesz rzutu kością.

Grabież

Grabież wymaga knary. Rzuć kością i zwiększ wynik wyłącznie przedstawionymi kartami broni. Kamienie nie są dozwolone.

Możesz wziąć żeton specjalny o wartości miecza równej lub niższej od końcowego wyniku. W razie porażki otrzymujesz po jednej karcie każdego rodzaju broni pokazanego na polu.

Grabież jest odrębną akcją od Najazdu, Rabunku i Plądrowania. Karta wymieniająca jedną nazwę nie działa automatycznie na Grabież.

Mała emigracja

Mała emigracja usuwa jeden twój wielorybnik. Weź żeton małej emigracji i połóż go na stole biesiadnym. Akcja nie wymaga srebra, chyba że jest wykonywana jako dodatkowa część innego pola z wyraźnie wskazaną opłatą.

Ruda leżąca na usuwanym wielorybniku przepada. Mała emigracja nie jest zwykłą emigracją, dlatego efekty kart odnoszące się do emigracji nie muszą jej obejmować.

Żetony punktów zwycięstwa

Gdy możesz zagrać kartę pomocnika, możesz zamiast tego odrzucić jedną kartę z ręki, aby zdobyć dostępny żeton PZ.

Zawsze bierzesz najwyższą dostępną wartość: najpierw 4 PZ, potem 3 PZ, następnie 2 PZ. Żetony 2 PZ są traktowane jako nieograniczone — po wyczerpaniu należy zapisywać kolejne.

Zasady szop rzemieślniczych

Szopy rzemieślnicze są szopami, ale nie domami. Układa się na nich dobra zgodnie ze standardowymi zasadami, z uwzględnieniem pól wymagających konkretnego elementu.

Niektóre pola można zakryć wyłącznie wskazanym zwierzęciem, mięsem wieloryba albo wielorybnikiem. Wielorybnik położony na szopie zostaje wyłączony z gry, a znajdująca się na nim ruda przepada.

Dobra położone na specjalnych polach magazynowych można później zdjąć i wykorzystać. Dla końcowego spełnienia warunku liczy się stan na koniec gry.

Ogrodzony obszar po lewej stronie wybranych szop nie jest uwzględniany przy wyznaczaniu ich linii dochodu.

Punkty nadrukowane na zwierzęciu albo łodzi położonej na szopie nie są liczone osobno w punktacji końcowej.

Karty pomocników a dodatek

Obrazek i tekst na karcie są nadrzędne. Ogólne reguły:

• klacze i świnie są czerwonymi dobrami oraz zwierzętami
• szopa rzemieślnicza jest szopą, ale nie domem
• łodzie i dobra leżące na szopie rzemieślniczej nie wpływają na efekty kart
• wzmianka o Najeździe albo Rabunku nie obejmuje Grabieży
• stare symbole wysp na kartach zachowują znaczenie mimo zmienionej grafiki pól

W przypadku konkretnej numerowanej karty należy korzystać z tabeli ujednolicenia dodatku. Aplikacja umożliwia znalezienie typowych wyjątków przez FAQ lub wyszukiwarkę; pełna tabela kart pozostaje PARTIAL.

Przykład przy stole

Masz jeszcze trzech wikingów. Wybierasz piątą kolumnę za 2 wikingów, wykonujesz pole i grasz pomocnika — potem spasujesz, nawet jeśli został ci jeden wiking. Alternatywnie najpierw robisz zwykłe akcje 1–4, a piątą kolumnę zostawiasz na domknięcie.

Typowy błąd

Używać piątej kolumny za wcześnie; mylić Grabież z Najazdem; liczyć punkty z łodzi na szopie rzemieślniczej; traktować małą emigrację jak zwykłą emigrację na kartach.

Wyjątek i odsyłacz

Fazy rundy, układanie i punktacja bazowe pozostają z rozdziałów 4–7. Solo łączy się z Norwegami przy 1 graczu.

Podsumowanie i mikro-FAQ

Jeden rozdział = cały dodatek. P: Czy 5. kolumna kończy akcje? O: Tak. P: Czy świnie mają ciążę? O: Nie — przy 2+ dostajesz 1 nową. P: Czy Grabież = Najazd? O: Nie. P: Czy szopa rzemieślnicza to dom? O: Nie.`
      },
    ],
  },
  {
    id: 'solo',
    title: '9. Wariant jednoosobowy',
    summary: 'Blokowanie akcji własnymi wikingami z poprzedniej rundy.',
    requiredModuleIds: ['base', 'solo'],
    sections: [
      {
        id: 'solo-flow',
        title: 'Zasada blokowania pól',
        requiredModuleIds: ['base', 'solo'],
        keywords: ['solo', 'blokowanie', 'kolor wikingów'],
        body: `Co to jest i po co

Wariant solo nie dodaje automy. Blokujesz sobie przyszłe pola własnymi wcześniejszymi wyborami — uczysz się planować dwie rundy naprzód.

Procedura

1. Użyj dwóch zestawów wikingów albo dwóch rozróżnialnych grup zgodnie z przygotowaniem wariantu.
2. W każdej rundzie wykonuj akcje jednym aktywnym zestawem.
3. Wikingowie użyci w bieżącej rundzie pozostają na planszy akcji i blokują te pola w następnej rundzie.
4. Na początku kolejnej rundy aktywuj drugi zestaw.
5. Po zakończeniu tej rundy zdejmij wikingów z pierwszego zestawu, dzięki czemu pola będą dostępne później.

Przykład przy stole

Runda 1 (zestaw A): zajmujesz polowanie i budowę. Runda 2 (zestaw B): te dwa pola są zablokowane przez A. Po rundzie 2 zdejmujesz A — w rundzie 3 znów będą wolne, a B będzie blokować to, co zrobiłeś w rundzie 2.

Typowy błąd

Traktować blokujących wikingów jak neutralnego przeciwnika albo zdejmować ich zbyt wcześnie.

Wyjątek i odsyłacz

Kolejność faz jak w grze wieloosobowej. Norwegowie i piąta kolumna działają normalnie, jeśli dodatek jest aktywny.

Podsumowanie i mikro-FAQ

Naprzemienne zestawy; blokada = twoja poprzednia runda. P: Czy to gra przeciw automie? O: Nie. P: Kiedy zdejmuję blokadę? O: Po rozegraniu rundy drugim zestawem, zgodnie z rytmem.`
      },
      {
        id: 'solo-scoring',
        title: 'Cel i punktacja solo',
        requiredModuleIds: ['base', 'solo'],
        keywords: ['solo', 'wynik', 'rekord'],
        body: `Cel

Rozgrywkę przeprowadza się przez normalną liczbę rund i liczy pełny wynik. Wariant solo jest próbą maksymalizacji rezultatu, a nie pojedynkiem z automą.

Porównywanie

Porównuj wyniki między partiami przy tej samej konfiguracji dodatków i długości gry. Wynik z Norwegami nie jest bezpośrednio porównywalny z wynikiem bez dodatku.

Przykład przy stole

Notujesz: 7 rund, Norwegowie włączone, wynik 62. Kolejna partia bez Norwegów = osobna tabela rekordów.

Typowy błąd

Porównywać rekordy z różnymi zestawami modułów albo skracać punktację.

Wyjątek i odsyłacz

Pełna punktacja: rozdział 7.

Podsumowanie i mikro-FAQ

Ten sam notes, cel = rekord. P: Czy są progi „zwycięstwa”? O: Oficjalne cele porównawcze trzymaj przy stole / w materiale wydania; aplikacja uczy procedury, nie inventuje tabeli medali. P: Czy mogę grać solo z Norwegami? O: Tak, przy 1 graczu i włączonym dodatku.`
      },
    ],
  },
];
